import React, { useEffect, useState, useMemo } from "react";
import Slider from "./Slider";
import { Link } from "react-router-dom";

import SizePicker from "../product-details/SizePicker";
import { useContextElement } from "../../context/Context";
import QuantitySelect from "../components/QuantitySelect";
import SideAccordions from "./SideAccordions";

export default function Details({ product }) {
    const [quantity, setQuantity] = useState(1);
    const [selectedVariantGuid, setSelectedVariantGuid] = useState(null);
    const [activeColor, setActiveColor] = useState("Black");
    const [sizeWarning, setSizeWarning] = useState(false);

    const { addProductToCart, isAddedToCartProducts, reloadCart } = useContextElement();

    // Tüm ekranda hep productData kullan
    const productData = product?.formattedItem;



    // ❗ selectedVariant'ı productData.variants'tan bul
    const selectedVariant = useMemo(() => {
        return productData?.variants?.find(v => v.guid === selectedVariantGuid) || null;
    }, [productData, selectedVariantGuid]);

    // max stok: seçili varyant varsa ondan, yoksa 5 (fallback)
    const maxStock = useMemo(() => {
        const n = Number(selectedVariant?.quantity);
        return Number.isFinite(n) ? n : 5;
    }, [selectedVariant]);

    // en üste, component içinde
    const allOutOfStock = useMemo(
        () => !(productData?.variants?.some(v => Number(v.quantity) > 0)),
        [productData]
    );

    const canAddToCart =
        !!selectedVariantGuid && Number(selectedVariant?.quantity) > 0 && !allOutOfStock;


    // Ürün geldiğinde default bir beden seç (stok önceliği ile)
    useEffect(() => {
        if (!productData?.variants?.length) return;
        if (allOutOfStock) { setSelectedVariantGuid(null); return; }


        // M, L, S, XL, XS önceliğiyle stokta olanı seç
        const sizePriority = ["M", "L", "S", "XL", "XS"];
        const inStockVariant =
            sizePriority
                .map(sz => productData.variants.find(v => v.size === sz && v.isStock && v.quantity > 0))
                .find(Boolean) ||
            productData.variants.find(v => v.isStock && v.quantity > 0) || // herhangi stoklu
            productData.variants[0]; // hepsi stocksuzsa ilkini seç (buton yine disabled olur)

        setSelectedVariantGuid(inStockVariant?.guid || null);
    }, [productData]);

    // Varyant değişince miktar stok üstüne çıkmasın
    useEffect(() => {
        if (!selectedVariant) return;
        if (quantity > maxStock) setQuantity(Math.max(1, maxStock));
        if (maxStock <= 0) setQuantity(1);
    }, [selectedVariantGuid, maxStock]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!productData) return null;

    return (
        <section>
            <div className="tf-main-product section-image-zoom">
                <div className="container">
                    <div className="row">
                        {/* Images */}
                        <div className="col-md-6">
                            <div className="tf-product-media-wrap sticky-top">
                                <div className="thumbs-slider">
                                    <Slider
                                        activeColor={activeColor}
                                        firstItem={productData.images?.[0]?.itemImages?.[0]}
                                        setActiveColor={setActiveColor}
                                        slideItems={
                                            productData.images?.[0]?.itemImages?.map((img, i) => ({
                                                id: i + 1,
                                                color: activeColor,
                                                size: "default",
                                                imgSrc: img,
                                            })) || []
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="col-md-6">
                            <div className="tf-product-info-wrap position-relative">
                                <div className="tf-zoom-main" />
                                <div className="tf-product-info-list other-image-zoom">
                                    <div className="tf-product-info-heading">
                                        <span className="brand-product">{productData.category?.name}</span>
                                        <h5 className="product-info-name fw-medium">{productData.name}</h5>
                                        <div className="product-info-price">
                                            <div className="display-sm price-new price-on-sale">
                                                £{Number(productData.totalPrice ?? productData.price ?? 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="tf-product-info-variant">
                                        <SizePicker
                                            selectedGuid={selectedVariantGuid}
                                            onSelect={setSelectedVariantGuid}
                                            variants={productData.variants || []}
                                        />
                                    </div>

                                    <div className="tf-product-total-quantity">
                                        {sizeWarning && (
                                            <div style={{ marginBottom: 10, color: "red", fontSize: 14, fontWeight: 500 }}>
                                                Please select a size before adding to cart!
                                            </div>
                                        )}

                                        <div className="group-btn">
                                            <QuantitySelect
                                                quantity={quantity}
                                                setQuantity={setQuantity}
                                                min={1}
                                                max={maxStock}
                                            />

                                            <a
                                                href={canAddToCart ? "#shoppingCart" : undefined}
                                                {...(canAddToCart ? {"data-bs-toggle": "offcanvas"} : {})}
                                                onClick={async (e) => {
                                                    if (!canAddToCart) {
                                                        e.preventDefault();
                                                        // stok tamamen bittiyse uyarı göstermeye gerek yok
                                                        setSizeWarning(!allOutOfStock && !selectedVariantGuid);
                                                        return;
                                                    }
                                                    setSizeWarning(false);
                                                    await addProductToCart(productData.slug, quantity, selectedVariantGuid);
                                                    await reloadCart();
                                                }}
                                                className={`tf-btn hover-primary btn-add-to-cart ${!canAddToCart ? "disabled opacity-50 pointer-events-none" : ""}`}
                                                aria-disabled={!canAddToCart}
                                            >
                                                {allOutOfStock
                                                    ? "Out of stock"
                                                    : isAddedToCartProducts(productData.slug, selectedVariantGuid)
                                                        ? "Already Added"
                                                        : "Add to cart"}
                                            </a>

                                        </div>
                                    </div>
                                </div>

                                <SideAccordions detail={productData.detail} care={productData.care}/>

                                <div className="tf-product-info-delivery-return">
                                    <div className="product-delivery">
                                        <div className="icon icon-car2"/>
                                        <p className="text-md">
                                            Estimated delivery time:{" "}
                                            <span className="fw-medium">3-5 days to UK</span>
                                        </p>
                                    </div>
                                    <div className="product-delivery">
                                        <div className="icon icon-shipping3" />
                                        <p className="text-md">
                                            Free shipping on <span className="fw-medium">all orders</span>
                                        </p>
                                    </div>
                                </div>

                                <div className="tf-product-info-trust-seal text-center">
                                    <p className="text-md text-dark-2 text-seal fw-medium">Guarantee Safe Checkout:</p>
                                    <ul className="list-card">
                                        <li className="card-item"><img alt="card" src="/images/payment/Visa.png" width={90} height={64} /></li>
                                        <li className="card-item"><img alt="card" src="/images/payment/Mastercard.png" width={90} height={64} /></li>
                                        <li className="card-item"><img alt="card" src="/images/payment/Stripe.png" width={90} height={64} /></li>
                                        <li className="card-item"><img alt="card" src="/images/payment/GooglePay.png" width={90} height={64} /></li>
                                        <li className="card-item"><img alt="card" src="/images/payment/ApplePay.png" width={90} height={64} /></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        {/* /Info */}
                    </div>
                </div>
            </div>
        </section>
    );
}
